import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Skeleton,
  Space,
  Steps,
  Tag,
  Typography,
  theme,
  type UploadFile
} from 'antd'
import { AxiosError } from 'axios'
import {
  ArrowLeft,
  Check,
  Database,
  File,
  LayoutGrid,
  Mail,
  Plus,
  Shield,
  Users
} from 'lucide-react'
import { useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  integrationRpcPresetsListOptions,
  organizationsAccessAddCreateMutation,
  organizationsOrganizationsAuthMethodsPartialUpdateMutation,
  organizationsOrganizationsCreateMutation,
  organizationsOrganizationsEnvironmentBuildCreateMutation,
  type OrganizationCreate
} from 'src/api/main'
import { DomainNameFormItem } from 'src/components'
import { providerOrder } from 'src/features/auth/providers'
import { SetupProgressStepperModal } from 'src/features/organizations/components'
import { demoPresetsInfo } from 'src/features/organizations/components/IndustrySelect/demoPresetsInfo'
import { AuthMethodsFieldset } from 'src/features/organizations/components/OrganizationSettings/AuthMethodsFieldset'
import { buildAuthMethodsPayload } from 'src/features/organizations/components/OrganizationSettings/authMethodsPayload'
import { useUpdateUserContext } from 'src/features/organizations/hooks'
import { buildWorkspaceInviteUrl } from 'src/features/workspaces/utils/inviteUrl'
import { useDocumentTitle, usePresignedUpload } from 'src/hooks'
import { paths } from 'src/router'
import { useShowApiErrors } from 'src/utils'
import { uploadFiles } from 'src/utils/uploadFiles'

import { useStyles } from '../styles'

import type { EmailInviteFormHandle } from './EmailInviteForm'
import { useInviteMembers } from './hooks'
import { InviteMembersStep } from './InviteMembersStep'
import { LogoUploadField } from './LogoUploadField'

const { Paragraph, Text, Title } = Typography

const NO_DATA_PRESET_CODENAME = 'no_data'
const BUILTIN_PRESET_FALLBACK_ID = 1

const STEP_FIELDS: string[][] = [
  ['name', 'domain_name'],
  ['workspace_template'],
  [
    'password_enabled',
    'email_otp_enabled',
    'google_enabled',
    'google_client_id',
    'google_client_secret',
    'entraid_enabled',
    'entraid_client_id',
    'entraid_client_secret',
    'entraid_tenant_id',
    'apple_enabled',
    'apple_client_id',
    'apple_client_secret',
    'apple_tenant_id',
    'apple_key_id'
  ],
  []
]

const FIELD_TO_STEP: Record<string, number> = {
  is_demo: 1,
  ...STEP_FIELDS.reduce<Record<string, number>>((acc, fields, stepIndex) => {
    for (const field of fields) acc[field] = stepIndex

    return acc
  }, {})
}

const findEarliestErrorStep = (error: unknown): number | null => {
  let data: unknown
  if (error instanceof AxiosError && error.response) {
    data = error.response.data
  } else if (
    typeof error === 'object' &&
    error !== null &&
    !('stack' in error)
  ) {
    data = error
  } else {
    return null
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return null
  }
  let earliest: number | null = null
  for (const field of Object.keys(data)) {
    const step = FIELD_TO_STEP[field]
    if (step === undefined) continue
    if (earliest === null || step < earliest) earliest = step
  }

  return earliest
}

const toWorkspaceDomainSuggestion = (name: string) => {
  const normalized = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  if (!normalized) return ''

  return /^[a-z]/.test(normalized) ? normalized : `w-${normalized}`
}

type DemoPresetKey = keyof typeof demoPresetsInfo
const isDemoPresetKey = (v: string): v is DemoPresetKey => v in demoPresetsInfo

type WorkspacesNewPageProps = {
  /**
   * First-ever workspace creation (user has none yet). Hides affordances that
   * would be dead links with no existing workspaces — the "back to list" link
   * and the step-1 cancel button. Set by the route chrome gate.
   */
  firstRun?: boolean
}

type WorkspacesNewFormValues = Record<string, unknown> & {
  name?: string
  photo?: UploadFile[]
  preset?: number | string | null
  workspace_template?: 'built_in' | 'clean'
}

export const WorkspacesNewPage = ({
  firstRun = false
}: WorkspacesNewPageProps) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [maxVisitedStep, setMaxVisitedStep] = useState(0)
  const [progressJobId, setProgressJobId] = useState<number>()
  const [progressOpen, setProgressOpen] = useState(false)
  const [createdOrganization, setCreatedOrganization] =
    useState<OrganizationCreate>()
  const [form] = Form.useForm<WorkspacesNewFormValues>()
  const [isDomainCustomized, setIsDomainCustomized] = useState(false)

  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { t } = useTranslation()
  const navigate = useNavigate()

  useDocumentTitle(t('workspaces.create.title'))

  const { updateUser, isPendingUser } = useUpdateUserContext()
  const { invites, addInvite, removeInvite, updateInviteRole } =
    useInviteMembers()
  const inviteFormRef = useRef<EmailInviteFormHandle>(null)

  const submitWithDraftFlush = () => {
    flushSync(() => {
      inviteFormRef.current?.commitDraft()
    })
    form.submit()
  }
  const { submitWebhook } = usePresignedUpload()

  const workspaceTemplate = Form.useWatch('workspace_template', form)
  const selectedPreset = Form.useWatch('preset', form)
  const nameForLogo: string = Form.useWatch('name', form) ?? ''

  const hasAnyAuthMethod = () =>
    providerOrder.some((m) => Boolean(form.getFieldValue(`${m}_enabled`)))

  const { data, isLoading } = useQuery({
    ...integrationRpcPresetsListOptions()
  })

  const { mutateAsync: addOrganization, isPending: isPendingOrganization } =
    useMutation({
      ...organizationsOrganizationsCreateMutation(),
      onError: (e) => {
        const earliest = findEarliestErrorStep(e)
        if (earliest !== null && earliest !== currentStep) {
          setCurrentStep(earliest)
          setMaxVisitedStep((m) => Math.max(m, earliest))
          message.warning(t('workspaces.create.submitFailedSwitchedStep'))
        }
        showApiErrors(e, form)
      }
    })

  const { mutateAsync: addDemoData, isPending: isPendingDemoData } =
    useMutation({
      ...organizationsOrganizationsEnvironmentBuildCreateMutation(),
      onError: (e) => showApiErrors(e, form)
    })

  const { mutateAsync: updateAuthMethods } = useMutation({
    ...organizationsOrganizationsAuthMethodsPartialUpdateMutation()
  })

  const { mutateAsync: sendInvites } = useMutation({
    ...organizationsAccessAddCreateMutation()
  })

  const isPending = isPendingUser || isPendingOrganization || isPendingDemoData

  const sortedPresets = (data?.results ?? [])
    .filter((p) => p.codename !== NO_DATA_PRESET_CODENAME)
    .slice()
    .sort((a, b) => {
      const orderA = isDemoPresetKey(a.codename)
        ? demoPresetsInfo[a.codename].order
        : 999
      const orderB = isDemoPresetKey(b.codename)
        ? demoPresetsInfo[b.codename].order
        : 999

      return orderA - orderB
    })

  const goNext = async () => {
    try {
      await form.validateFields(STEP_FIELDS[currentStep])
      if (currentStep === 2 && !hasAnyAuthMethod()) {
        message.warning(t('workspaces.create.authorization.atLeastOneMethod'))

        return
      }
      const next = Math.min(currentStep + 1, 3)
      setCurrentStep(next)
      setMaxVisitedStep((m) => Math.max(m, next))
    } catch {
      /* validation errors shown by form */
    }
  }

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0))
  const handleCloseProgress = () => {
    setProgressOpen(false)

    if (createdOrganization?.id) {
      navigate(
        paths.workspaces.detailsTab(String(createdOrganization.id), 'overview')
      )
    }
  }

  const onFinish = async (values: WorkspacesNewFormValues) => {
    if (!hasAnyAuthMethod()) {
      message.warning(t('workspaces.create.authorization.atLeastOneMethod'))
      setCurrentStep(2)

      return
    }

    const photoField = values.photo as UploadFile[] | undefined
    let photoId: number | null = null
    if (photoField && photoField.length > 0) {
      try {
        await uploadFiles(photoField, {
          submitWebhook,
          onError: (error) => showApiErrors(error)
        })
      } catch (error) {
        showApiErrors(error)

        return
      }
      const uploadResponse = photoField[0]?.response as
        | { id?: number }
        | undefined
      photoId = uploadResponse?.id ?? null
    }

    const { photo: _photoForm, ...valuesWithoutPhoto } = values
    const templateMode = values.workspace_template
    const isCleanWorkspace = templateMode === 'clean'

    let organization: OrganizationCreate | undefined
    try {
      organization = (await addOrganization({
        body: {
          ...valuesWithoutPhoto,
          photo: photoId,
          is_demo: true
        } as unknown as OrganizationCreate
      })) as OrganizationCreate | undefined
    } catch {
      return
    }

    if (!organization?.id) return

    const presetRaw = values.preset
    const presetFromForm =
      typeof presetRaw === 'number'
        ? presetRaw
        : typeof presetRaw === 'string'
          ? Number(presetRaw)
          : undefined
    const presetResolved =
      Number.isFinite(presetFromForm) && presetFromForm != null
        ? presetFromForm
        : BUILTIN_PRESET_FALLBACK_ID

    const orgId = Number(organization.id)

    const inviteGroups: Array<[string, string[]]> = []
    if (invites.length > 0) {
      const byRole = new Map<string, string[]>()
      for (const inv of invites) {
        const bucket = byRole.get(inv.orgRole) ?? []
        bucket.push(inv.email)
        byRole.set(inv.orgRole, bucket)
      }
      inviteGroups.push(...byRole.entries())
    }
    const inviteUrl = buildWorkspaceInviteUrl(organization.domain_name)

    const demoDataResultPromise = isCleanWorkspace
      ? null
      : Promise.allSettled([
          addDemoData({
            path: { id: orgId },
            // Workspace creation with a built-in preset should provision the
            // preset's required apps. Recreate flows must not pass this flag.
            // Cast: regenerate OpenAPI types after the backend contract lands.
            body: {
              preset: presetResolved,
              install_missing_applications: true
            } as { preset: number | null }
          })
        ]).then(([result]) => result)

    const [, authResult, ...inviteResults] = await Promise.allSettled([
      updateUser({}),
      updateAuthMethods({
        path: { id: orgId },
        body: { auth_methods: buildAuthMethodsPayload(values) }
      }),
      // Grouped by role only to batch the addresses: the invite endpoint no
      // longer takes a tier — it is `readonly` on the access record — so every
      // group is sent the same way and the chosen role does not travel.
      ...inviteGroups.map(([, emails]) =>
        sendInvites({
          body: {
            organization: orgId,
            emails,
            invite_url: inviteUrl
          }
        })
      )
    ])

    const demoDataResult = demoDataResultPromise
      ? await demoDataResultPromise
      : null

    message.success(t('organization_add_success'))

    if (demoDataResult?.status === 'rejected') {
      message.warning(t('workspaces.create.demoDataFailed'))
    }

    if (authResult.status === 'rejected') {
      showApiErrors(authResult.reason)
      message.error(t('workspaces.create.auth.applyFailed'))
      form.resetFields()
      navigate(paths.workspaces.security(String(orgId)))

      return
    }

    if (invites.length > 0) {
      const anyInviteFailed = inviteResults.some((r) => r.status === 'rejected')
      if (anyInviteFailed) {
        message.warning(t('workspaces.create.invite.sendFailed'))
      } else {
        message.success(
          t('workspaces.create.invite.sent', { count: invites.length })
        )
      }
    }

    form.resetFields()

    if (demoDataResult?.status === 'fulfilled' && demoDataResult.value?.id) {
      setCreatedOrganization(organization)
      setProgressJobId(demoDataResult.value.id)
      setProgressOpen(true)

      return
    }

    navigate(paths.workspaces.detailsTab(String(orgId), 'overview'))
  }

  const renderSectionHeader = (title: string, subtitle?: string) => (
    <div className={styles.sectionHeader}>
      <Title level={5} className={styles.sectionTitle}>
        {title}
      </Title>
      {subtitle ? (
        <Paragraph className={styles.sectionSubtitle}>{subtitle}</Paragraph>
      ) : null}
    </div>
  )

  const renderStepBasics = () => (
    <div key="basics" className={styles.stepContent}>
      {renderSectionHeader(
        t('workspaces.create.details.title'),
        t('workspaces.create.details.subtitle')
      )}

      <LogoUploadField fallbackName={nameForLogo} />

      <div className={styles.fieldGrid}>
        <Form.Item
          label={t('workspaces.create.details.nameLabel')}
          name="name"
          rules={[{ required: true }]}
          extra={
            <Text className={styles.fieldHelper}>
              {t('workspaces.create.details.nameHelper')}
            </Text>
          }
        >
          <Input
            placeholder={t('workspaces.create.details.namePlaceholder')}
            autoComplete="organization"
            size="large"
            onChange={(event) => {
              if (isDomainCustomized) return
              const suggestedDomain = toWorkspaceDomainSuggestion(
                event.target.value
              )
              form.setFieldValue('domain_name', suggestedDomain)
            }}
          />
        </Form.Item>

        <DomainNameFormItem
          name="domain_name"
          label={t('workspaces.create.details.domainLabel')}
          extra={
            <Text className={styles.fieldHelper}>
              {t('workspaces.create.details.domainHelper')}
            </Text>
          }
          inputProps={{
            size: 'large',
            placeholder: t('workspaces.create.details.domainPlaceholder'),
            onChange: () => setIsDomainCustomized(true)
          }}
        />
      </div>
    </div>
  )

  const renderStepTemplate = () => (
    <div key="template" className={styles.stepContent}>
      {renderSectionHeader(t('workspaces.create.template.label'))}

      <Form.Item name="workspace_template" noStyle rules={[{ required: true }]}>
        <Input type="hidden" />
      </Form.Item>

      <Flex gap={12} wrap style={{ marginBottom: 20 }}>
        <div
          className={cx(
            styles.templateOption,
            workspaceTemplate === 'built_in' && styles.templateOptionSelected
          )}
          onClick={() => form.setFieldValue('workspace_template', 'built_in')}
        >
          <Flex align="center" gap={14}>
            <Database
              size={26}
              color={
                workspaceTemplate === 'built_in'
                  ? token.colorPrimary
                  : token.colorTextSecondary
              }
            />
            <Flex vertical gap={2} flex={1}>
              <Typography.Text strong>
                {t('workspaces.create.template.withBuiltIn')}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspaces.create.template.builtInPreset')}
              </Typography.Text>
            </Flex>
            {workspaceTemplate === 'built_in' && (
              <div className={styles.checkBadge}>
                <Check size={14} />
              </div>
            )}
          </Flex>
        </div>

        <div
          className={cx(
            styles.templateOption,
            workspaceTemplate === 'clean' && styles.templateOptionSelected
          )}
          onClick={() => form.setFieldValue('workspace_template', 'clean')}
        >
          <Flex align="center" gap={14}>
            <File
              size={26}
              color={
                workspaceTemplate === 'clean'
                  ? token.colorPrimary
                  : token.colorTextSecondary
              }
            />
            <Flex vertical gap={2} flex={1}>
              <Typography.Text strong>
                {t('workspaces.create.template.clean')}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspaces.create.template.cleanDescription')}
              </Typography.Text>
            </Flex>
            {workspaceTemplate === 'clean' && (
              <div className={styles.checkBadge}>
                <Check size={14} />
              </div>
            )}
          </Flex>
        </div>
      </Flex>

      {workspaceTemplate === 'built_in' && (
        <Flex vertical gap={10}>
          <Typography.Text strong>
            {t('workspaces.create.template.builtInPreset')}
          </Typography.Text>

          <Form.Item name="preset" noStyle>
            <Input type="hidden" />
          </Form.Item>

          {isLoading ? (
            <div className={styles.presetGrid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton.Button key={i} active block style={{ height: 44 }} />
              ))}
            </div>
          ) : (
            <div className={styles.presetGrid}>
              {sortedPresets.map((preset) => {
                const info = isDemoPresetKey(preset.codename)
                  ? demoPresetsInfo[preset.codename]
                  : null
                const Icon = info?.icon
                const color = info
                  ? (token[info.colorToken] as string)
                  : token.colorTextSecondary
                const isSelected = selectedPreset === preset.id

                return (
                  <div
                    key={preset.id}
                    className={cx(
                      styles.presetCard,
                      isSelected && styles.presetCardSelected
                    )}
                    onClick={() => form.setFieldValue('preset', preset.id)}
                  >
                    <Flex align="center" gap={10}>
                      {Icon && (
                        <Icon style={{ fontSize: 20, color, flexShrink: 0 }} />
                      )}
                      <Typography.Text
                        strong={isSelected}
                        style={{ flex: 1, fontSize: 13 }}
                      >
                        {preset.name}
                      </Typography.Text>
                      {isSelected && (
                        <div className={styles.checkBadge}>
                          <Check size={14} />
                        </div>
                      )}
                    </Flex>
                  </div>
                )
              })}
            </div>
          )}
        </Flex>
      )}
    </div>
  )

  const renderStepAuth = () => (
    <div key="auth" className={styles.stepContent}>
      {renderSectionHeader(
        t('workspaces.create.authorization.methodsLabel'),
        t('workspaces.create.authorization.methodsHint')
      )}

      <AuthMethodsFieldset requireClientSecret />

      <Divider style={{ margin: '24px 0 16px' }} />

      <Flex vertical gap={12}>
        <Flex justify="space-between" align="center">
          <Space size={8}>
            <Typography.Text strong>
              {t('workspaces.create.authorization.customTitle')}
            </Typography.Text>
            <Tag color="warning">{t('soon')}</Tag>
          </Space>
          <Button icon={<Plus size={14} />} size="small" disabled>
            {t('authMethods.addCustomProvider')}
          </Button>
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t('authMethods.customProvidersCardDescription')}
        </Typography.Text>
      </Flex>
    </div>
  )

  const renderStepInvite = () => (
    <div key="invite" className={styles.stepContent}>
      <InviteMembersStep
        ref={inviteFormRef}
        invites={invites}
        addInvite={addInvite}
        removeInvite={removeInvite}
        updateInviteRole={updateInviteRole}
      />
    </div>
  )

  const stepItems = [
    {
      title: t('workspaces.create.steps.basics'),
      icon: <Users size={14} />
    },
    {
      title: t('workspaces.create.steps.template'),
      icon: <LayoutGrid size={14} />
    },
    {
      title: t('workspaces.create.steps.security'),
      icon: <Shield size={14} />
    },
    {
      title: t('workspaces.create.invite.stepTitle'),
      icon: <Mail size={14} />
    }
  ]

  return (
    <div className={styles.container}>
      {!firstRun && (
        <Link to={paths.workspaces.root} className={styles.backLink}>
          <ArrowLeft size={14} />
          {t('workspaces.backToList')}
        </Link>
      )}

      <div className={styles.pageHeader}>
        <span className={styles.eyebrow}>{t('workspaces.create.eyebrow')}</span>
        <Title level={3} style={{ margin: 0 }}>
          {t('workspaces.create.title')}
        </Title>
        <Paragraph className={styles.pageDescription}>
          {t('workspaces.create.description')}
        </Paragraph>
      </div>

      <div className={styles.card}>
        <div className={styles.stepsWrap}>
          <Steps
            current={currentStep}
            items={stepItems}
            size="small"
            onChange={(step) => {
              if (step <= maxVisitedStep) {
                setCurrentStep(step)
              }
            }}
          />
        </div>

        <Form
          form={form}
          requiredMark={false}
          scrollToFirstError
          layout="vertical"
          onFinish={(vals) => void onFinish(vals)}
          initialValues={{
            workspace_template: 'built_in',
            password_enabled: true,
            email_otp_enabled: false,
            google_enabled: false,
            entraid_enabled: false,
            apple_enabled: false
          }}
        >
          <div className={styles.body}>
            <div style={{ display: currentStep === 0 ? undefined : 'none' }}>
              {renderStepBasics()}
            </div>
            <div style={{ display: currentStep === 1 ? undefined : 'none' }}>
              {renderStepTemplate()}
            </div>
            <div style={{ display: currentStep === 2 ? undefined : 'none' }}>
              {renderStepAuth()}
            </div>
            <div style={{ display: currentStep === 3 ? undefined : 'none' }}>
              {renderStepInvite()}
            </div>
          </div>
        </Form>

        <div className={styles.footer}>
          <Flex justify="space-between" align="center" gap={12}>
            <Text className={styles.stepCounter}>
              {t('workspaces.create.stepCounter', {
                current: currentStep + 1,
                total: stepItems.length
              })}
            </Text>

            <Flex gap={8}>
              {currentStep > 0 ? (
                <Button onClick={goBack}>{t('back')}</Button>
              ) : firstRun ? null : (
                <Button onClick={() => navigate(paths.workspaces.root)}>
                  {t('cancel')}
                </Button>
              )}

              {currentStep < 3 ? (
                <Button type="primary" onClick={goNext}>
                  {t('continue')}
                </Button>
              ) : (
                <Flex gap={8}>
                  <Button onClick={() => form.submit()}>
                    {t('workspaces.create.invite.skipForNow')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={submitWithDraftFlush}
                    loading={isPending || isLoading}
                  >
                    {t('workspaces.create.invite.sendAndFinish')}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
        </div>
      </div>

      <SetupProgressStepperModal
        key={progressJobId ?? 'idle'}
        open={progressOpen}
        onCancel={handleCloseProgress}
        parentId={progressJobId}
        organization={createdOrganization}
      />
    </div>
  )
}
